import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EChartsCoreOption } from 'echarts/core';
import type { QuerySpec, DateBucket } from '@/types/query';
import type { ChartOptions } from '@/types/widget';
import { useQueryResult } from '@/hooks/useQueryResult';
import { useEChartsInstance } from '@/hooks/useEChartsInstance';
import { useUiStore } from '@/store/ui-store';
import { ChartExportButton } from '@/features/export/ChartExportButton';
import { useChartRegistration } from '@/features/export/chart-registry';
import {
  DEFAULT_AXIS_FONT_SIZE,
  axisLabelColor,
  formatAxisLabel,
  formatBucketedDate,
  formatNumber,
  paletteColors,
} from './chart-axis-config';
import { WidgetContainer } from './WidgetContainer';
import { ReloadButton } from './ReloadButton';

type PieChartWidgetProps = {
  /** Widget id, used by the chart-registry for whole-dashboard PDF export. */
  widgetId: string;
  title: string;
  spec: QuerySpec;
  onRemove?: (() => void) | undefined;
  onSettings?: (() => void) | undefined;
  options?: ChartOptions | undefined;
  note?: string | undefined;
  /** When the label column is a bucketed date column, this is the bucket. */
  dateBucket?: DateBucket | undefined;
};

/**
 * Resolve the CSS variables used for the in-canvas total badge into
 * literal hex / rgb values. ECharts' canvas renderer doesn't understand
 * `var(--…)`, so we need the computed values at option-build time so the
 * badge survives PNG export (item: pasta png indirildiğinde toplam görünsün).
 * Falls back to a light-theme palette when running outside the browser
 * (vitest / SSR) so the helper stays pure-safe.
 */
function readBadgeColors(): { fg: string; bg: string; border: string } {
  if (typeof window === 'undefined') {
    return { fg: '#0f172a', bg: '#ffffff', border: '#d4d4d8' };
  }
  const cs = getComputedStyle(document.documentElement);
  const pick = (name: string, fallback: string): string => {
    const v = cs.getPropertyValue(name).trim();
    return v.length > 0 ? v : fallback;
  };
  return {
    fg: pick('--color-fg', '#0f172a'),
    bg: pick('--color-bg', '#ffffff'),
    border: pick('--color-border', '#d4d4d8'),
  };
}

/**
 * Build the per-segment label formatter. ECharts' built-in `{c}` token
 * doesn't honour locale grouping, so we substitute it ourselves with
 * `formatNumber` while leaving `{b}` (name) and `{d}` (percent) intact.
 */
function buildPieLabelFormatter(
  format: ChartOptions['pieLabelFormat'],
): (p: { name: string; value: number; percent: number }) => string {
  return (p) => {
    const value = formatNumber(p.value);
    const percent = `${p.percent.toFixed(1)}%`;
    switch (format) {
      case 'percent':
        return percent;
      case 'value':
        return value;
      case 'name':
        return p.name;
      case 'name-value':
        return `${p.name}: ${value}`;
      case 'name-percent':
        return `${p.name}: ${percent}`;
      case 'percent-value':
        return `${percent} (${value})`;
      case 'both':
      default:
        return `${p.name}: ${value} (${percent})`;
    }
  };
}

/**
 * Pie / donut chart. Uses the first groupBy column as label
 * and the first metric as value.
 */
export function PieChartWidget({
  widgetId,
  title,
  spec,
  onRemove,
  onSettings,
  options,
  note,
  dateBucket,
}: PieChartWidgetProps): JSX.Element {
  const { t } = useTranslation();
  const { data, isLoading, error, reload } = useQueryResult(spec);
  const theme = useUiStore((s) => s.theme);
  const labelKey = spec.groupBy[0] ?? '';
  const isCompare = !!data?.compareMeta;
  const showTotal = options?.pieShowTotal === true;
  // Tracks which legend segments are currently visible. `null` until the
  // first legendselectchanged event — treated as "everything visible" so
  // the initial render shows the unfiltered total.
  const [legendSel, setLegendSel] = useState<Record<string, boolean> | null>(
    null,
  );
  // Date columns arrive as JS Date instances from DuckDB-WASM; calling
  // String() on them yields "Tue Jul 02 2024 …", which is the user-visible
  // garbage on pie segments. Reuse the same formatters bar/line use so the
  // segment names render as DD.MM.YYYY (or bucketed when dateBucket set).
  // Declared before any hook that references it — `const` hoisting puts
  // the binding in TDZ, so the legend-aware totalValue memo below would
  // throw on first render if this lived under it.
  const formatLabel = (value: unknown): string =>
    dateBucket ? formatBucketedDate(value, dateBucket) : formatAxisLabel(value);
  const totalValue = useMemo<number | null>(() => {
    if (!showTotal || !data) return null;
    const metricKey = spec.metrics[0]?.alias ?? '';
    let sum = 0;
    let any = false;
    for (const r of data.rows) {
      const segName = formatLabel(r[labelKey]);
      if (legendSel && legendSel[segName] === false) continue;
      const raw = r[metricKey];
      const n =
        typeof raw === 'number'
          ? raw
          : typeof raw === 'bigint'
            ? Number(raw)
            : Number(raw);
      if (Number.isFinite(n)) {
        sum += n;
        any = true;
      }
    }
    return any ? sum : null;
    // formatLabel is stable per render via its closure dependencies above
    // (dateBucket); listing it would force the formatter through the deps
    // array even though its identity doesn't change between rerenders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTotal, data, spec.metrics, labelKey, legendSel]);

  const option = useMemo<EChartsCoreOption | null>(() => {
    if (!data || data.rows.length === 0) return null;
    const metricKey = spec.metrics[0]?.alias ?? '';
    const textColor = axisLabelColor(theme);

    const showLegend = options?.showLegend !== false;
    const donut = options?.donut !== false;
    const labelPosition = options?.pieLabelPosition ?? 'outside';
    const labelFormat = options?.pieLabelFormat ?? 'both';

    // Reduce pie size if labels are outside to prevent cropping/overlap
    const isOutside = labelPosition === 'outside';
    const radius = donut
      ? (isOutside ? ['30%', '55%'] : ['40%', '70%'])
      : (isOutside ? [0, '55%'] : [0, '70%']);
    const colors = [...paletteColors(options?.colorPalette)];
    const labelFormatter = buildPieLabelFormatter(labelFormat);

    // Bake the "Toplam: N" badge into the chart canvas as an ECharts
    // `graphic` element so it's captured by `getDataURL()` — the previous
    // HTML overlay was invisible in the exported PNG (item: pasta png
    // indirildiğinde toplam görünsün). CSS vars don't resolve inside
    // canvas paint, so we read the literal computed values up front.
    const badgeColors = readBadgeColors();
    const totalBadge =
      totalValue != null
        ? [
            {
              type: 'text' as const,
              left: 8,
              top: 8,
              z: 100,
              style: {
                text: `${t('widgetSettings.pieTotalLabel')}: ${formatNumber(totalValue)}`,
                fontSize: DEFAULT_AXIS_FONT_SIZE,
                fill: badgeColors.fg,
                backgroundColor: badgeColors.bg,
                padding: [4, 8] as [number, number],
                borderColor: badgeColors.border,
                borderWidth: 1,
                borderRadius: 4,
              },
            },
          ]
        : [];

    return {
      color: colors,
      tooltip: {
        trigger: 'item',
        formatter: (p: { name: string; value: number; percent: number }) =>
          `${p.name}: ${formatNumber(p.value)} (${p.percent.toFixed(1)}%)`,
      },
      legend: {
        bottom: 0,
        show: showLegend,
        textStyle: { color: textColor, fontSize: DEFAULT_AXIS_FONT_SIZE },
        // Preserve the user's hide/show selections across the option
        // re-render that legendselectchanged → setLegendSel triggers.
        // Without this, `notMerge: true` in useEChartsInstance would
        // reset the legend state on every total-overlay refresh.
        ...(legendSel ? { selected: legendSel } : {}),
      },
      graphic: totalBadge,
      series: [
        {
          type: 'pie' as const,
          radius,
          center: ['50%', showLegend ? '42%' : '50%'],
          label: {
            show: true,
            position: labelPosition,
            formatter: labelFormatter,
            color: textColor,
            fontSize: DEFAULT_AXIS_FONT_SIZE,
          },
          labelLine: { show: labelPosition === 'outside' },
          emphasis: { label: { show: true, fontWeight: 'bold' } },
          data: data.rows.map((r) => ({
            name: formatLabel(r[labelKey]),
            value: r[metricKey] ?? 0,
          })),
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, labelKey, spec.metrics, theme, options, dateBucket, totalValue, legendSel, t]);

  const { chartRef, getInstance } = useEChartsInstance(option);
  useChartRegistration(widgetId, title, getInstance);

  // Wire the legend visibility map to the React-side total overlay so
  // toggling a segment off recomputes the displayed sum. Without this,
  // ECharts hides the slice + adjusts percent labels but the overlay
  // stays anchored to the unfiltered sum.
  useEffect(() => {
    if (!showTotal) return;
    const inst = getInstance();
    if (!inst) return;
    const handler = (...args: unknown[]): void => {
      const evt = args[0] as { selected?: Record<string, boolean> } | undefined;
      if (evt?.selected) setLegendSel({ ...evt.selected });
    };
    inst.on('legendselectchanged', handler);
    return () => {
      inst.off('legendselectchanged', handler);
    };
    // option drives the chart's existence — re-attach if it remounts.
  }, [showTotal, option, getInstance]);

  // Reset the legend tracker when the data shape changes — otherwise a
  // stale `legendSel` keyed on the previous category set would hide
  // every new segment.
  useEffect(() => {
    setLegendSel(null);
  }, [data, labelKey]);

  return (
    <WidgetContainer
      title={title}
      isLoading={isLoading}
      error={error}
      onRemove={onRemove}
      onSettings={onSettings}
      note={note}
      titleBarExtra={
        <div className="flex items-center gap-1.5">
          <ReloadButton onReload={reload} disabled={isLoading} />
          <ChartExportButton title={title} getInstance={getInstance} />
        </div>
      }
    >
      <div className="relative w-full h-full min-h-[200px]">
        {isCompare ? (
          <div
            className="absolute inset-0 flex items-center justify-center text-center text-xs px-4"
            style={{ color: 'var(--color-muted)' }}
            role="status"
          >
            {t('compare.pieUnsupported')}
          </div>
        ) : (
          <div
            ref={chartRef}
            className="w-full h-full"
            aria-label={
              totalValue != null
                ? `${t('widgetSettings.pieTotalAria')}: ${formatNumber(totalValue)}`
                : undefined
            }
          />
        )}
      </div>
    </WidgetContainer>
  );
}
